import * as React from "react";
import styled, { css } from "styled-components";
import * as moment from "moment-timezone";
import { useHistory } from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import { Text } from "src/library/components/Text/Text";
import { IOrder } from "src/core/orders/api/orders.api";
import { Button } from "src/library/components/Button";
import { Datetime } from "src/library/components/Datetime";
import { useOrderCutoffTime } from "src/core/orders/hooks/validation";
import { Icons } from "src/library/components/Icon/types";
import { Icon } from "src/library/components/Icon";
import { Tooltip } from "src/library/components/Tooltip";
import { IStore } from "src/core/shared/store";
import { useCanPayForOrder } from "src/modules/payment/hooks/useCanPayForOrder";
import { TotalPrice } from "src/core/prices/components/TotalPrice";

interface UpcomingPaymentRowProps {
  order: IOrder;
}

const UpcomingPaymentRow: React.ReactFCWithChildren<UpcomingPaymentRowProps> = ({ order }) => {
  const history = useHistory();
  const orderCutoffTime = useOrderCutoffTime();
  const preauthorizationsEnabled = !!useMappedState((store: IStore) => store.global.settings?.payment?.preauthorizationsEnabled);
  const canPayNow = useCanPayForOrder(order);

  const cutoffTime = React.useMemo(() => {
    return orderCutoffTime(order);
  }, [ order, orderCutoffTime ]);

  const cutoffCloseWarning = React.useMemo(() => {
    if (cutoffTime) {
      // for preauth orders allow payment up to 6 hours ahead of time
      if (preauthorizationsEnabled) {
        if (moment.utc(cutoffTime).diff(moment.utc(), "hours") < 6) {
          return "urgent";
        }

        return "still-time";
      }

      if (moment.utc().isAfter(cutoffTime)) {
        return "too-late";
      }
      const diffHours = moment.utc(cutoffTime).diff(moment.utc(), "hours");

      if (diffHours > 48) {
        return "still-time";
      } else if (diffHours > 6) {
        return "warn";
      } else {
        return "urgent";
      }
    }

    return "";
  }, [ cutoffTime, preauthorizationsEnabled ]);

  return (
    <UpcomingPaymentRowWrapper className={cutoffCloseWarning}>
      <div className="order-info">
        <Icon
          className="info-icon"
          icon={cutoffCloseWarning === "still-time" ? Icons.CALENDAR : Icons.WARN}
        />

        <div className="info-row">
          <Text
            weight="bold"
            data-tip
            data-for={`${order.id}-payment-by`}
          >
            {"Payment Due "}

            <Icon
              icon={Icons.HELP}
              size={10}
            />
          </Text>

          <Tooltip
            id={`${order.id}-payment-by`}
            type="dark"
            place="top"
            getContent={() => {
              return (
                <Text color="white">
                  If you do not pay for this order by the due date, you will not receive this order.
                </Text>
              );
            }}
          />

          <Text className={`time-to-pay ${cutoffCloseWarning}`}>
            {cutoffCloseWarning === "too-late" ?
              "Overdue" : (
                <Datetime
                  date={cutoffTime}
                  template="count_down"
                />
              )}
          </Text>
        </div>

        <div className="info-row">
          <Text weight="bold">
            Delivery Date
          </Text>

          <Text>
            <Datetime
              ignoreLocal
              date={order.orderDeliveryDate}
              format="DD/MM/YYYY"
            />
          </Text>
        </div>

        <div className="info-row">
          <Text weight="bold">
            Total
          </Text>

          <TotalPrice
            lines={order.orderLines}
            id={order.id}
            totalOnly
          />
        </div>

      </div>

      <div className="button-wrapper">
        {canPayNow && (
          <Button
            $buttonType="secondary"
            onClick={() => history.push(`orders/${order.id}/checkout`)}
          >
            Pay now
          </Button>
        )}
      </div>
    </UpcomingPaymentRowWrapper>
  );
};

export default UpcomingPaymentRow;

const UpcomingPaymentRowWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    padding: ${theme.spacing.padding.sm};
    border: 2px solid ${theme.colors.lightGrey};
    margin-bottom: ${theme.spacing.margins.sm};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .info-icon {
      margin-right: ${theme.spacing.padding.sm};
    }

    &.urgent, &.too-late {
      border-color: ${theme.colors.error};
      border-width: 3px; 

      svg.info-icon  {
        fill: ${theme.colors.error}!important;
      }

      .time-to-pay {
          color: ${theme.colors.error};
      }
    }

    &.warn {
      border-color: ${theme.colors.warn};
      border-width: 3px; 

      svg.info-icon  {
        fill: ${theme.colors.warn}!important;
      }

      .time-to-pay {
          color: ${theme.colors.warn};
      }
    }
    
    .order-info {
      display: flex;
      flex-direction: row;
      align-items: center;

      .info-row {
        display: flex;
        flex-direction: column;
        margin-right: ${theme.spacing.margins.sm};
      } 

      .time-to-pay {
        &.warn {
          color: ${theme.colors.warn};
        }
      }
    }
  `}
`;
